


































































import Vue, { PropType } from "vue";
import PlotItemCard from "@/components/atoms/PlotItemCard.vue";
import InputText from "@/components/atoms/InputText.vue";
import draggable from "vuedraggable";
import { Mode, Plot, PlotGroups, Plots, SubPlot, UpdatePlotPayload, UpdateSubPlotPayload } from "@/lib/models/plot";
import { v4 as uuidv4 } from "uuid";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { InputText, PlotItemCard, draggable },
  props: {
    novelId: String,
    plotId: String,
    plots: Object as PropType<Plot | SubPlot>,
  },
  data() {
    const { plotId, plots } = this;
    const { plotGroups } = plots;

    return {
      plotGroups,
      plotName: plotId ? (plots as SubPlot).name : null,
      maxDescriptionLength: 2500,
      changed: false,
    };
  },
  computed: {
    mode() {
      const { plots } = this;
      return plots.mode;
    },
    isCustom() {
      const { mode } = this;
      return mode === Mode.custom;
    },
  },
  methods: {
    setChanged(b) {
      this.changed = b;
      this.setModified(b);
    },
    input() {
      this.setChanged(true);
    },
    async inputItem(plot, plots) {
      const { generateInitialPlot } = this;
      const { isInitial } = plot;

      if (isInitial) {
        delete plot.isInitial;
        plots.push(generateInitialPlot());
      }

      this.input();
    },
    deleteItem(plot, index) {
      this.plotGroups[index].plots = this.plotGroups[index].plots.filter((x) => x.key !== plot.key);

      this.input();
    },
    onDragEnd() {
      this.setChanged(true);
    },
    addPlotGroup() {
      const { $notify, setChanged } = this;

      const plotGroup = {
        name: "自由入力",
        description: "説明文",
        plots: [],
      };
      this.plotGroups.push(plotGroup);

      $notify({
        title: "プロットグループが追加されました",
        type: "success",
      });
      setChanged(true);
    },
    async deletePlotGroup(index) {
      const { $notify, setChanged } = this;

      this.plotGroups = this.plotGroups.filter((_, i) => i !== index);

      $notify({
        title: "プロットグループを削除しました",
        type: "error",
      });
      setChanged(true);
    },
    generateInitialPlot() {
      const initialPlot: ExtensionPlot = {
        key: uuidv4(),
        text: "",
        isInitial: true,
      };
      return initialPlot;
    },
    async save() {
      const { $store, $router, $notify, novelId, plotId, mode, plotName, setChanged } = this;
      let { plotGroups } = this;

      // remove initial plot
      plotGroups = plotGroups.map((plotGroup) => {
        const { name, description, plots } = plotGroup;
        return {
          name,
          description,
          plots: (plots as ExtensionPlot[]).filter((plot) => !plot.isInitial),
        };
      });

      if (plotId && plotName) {
        const updateSubPlotPayload: UpdateSubPlotPayload = {
          novelId,
          plotId,
          name: plotName,
          plotGroups,
          mode,
        };
        $store.dispatch("plotModule/updateSubPlot", updateSubPlotPayload);
      } else {
        const updatePlotPayload: UpdatePlotPayload = {
          novelId,
          plotGroups,
        };
        $store.dispatch("plotModule/updatePlot", updatePlotPayload);
      }

      $notify({
        title: "保存しました",
        type: "success",
      });
      setChanged(false);

      if (plotId) {
        $router.push({ name: "plot", params: { novelId, plotId } });
      } else {
        $router.push({ name: "plot", params: { novelId } });
      }
    },
  },
  watch: {
    plotGroups: {
      handler() {
        const { plotGroups, generateInitialPlot } = this;

        plotGroups.forEach((plotGroup) => {
          // check if plotGroup has initial plot
          const hasInitialPlot = (plotGroup.plots as ExtensionPlot[]).some((plot) => plot.isInitial);
          if (!hasInitialPlot) {
            plotGroup.plots.push(generateInitialPlot());
          }

          // check if initial plot is last in the list
          const initialPlotIndex = (plotGroup.plots as ExtensionPlot[]).findIndex((plot) => plot.isInitial);
          if (plotGroup.plots.length - 1 !== initialPlotIndex) {
            const initialPlot = plotGroup.plots[initialPlotIndex];
            plotGroup.plots = plotGroup.plots.filter((_, index) => index !== initialPlotIndex);
            plotGroup.plots.push(initialPlot);
          }
        });
      },
      deep: true,
      immediate: true,
    },
  },
});

interface Props {
  novelId: string;
  plotId?: string;
  plots: Plot | SubPlot;
}

interface Data {
  plotGroups: PlotGroups[];
  plotName: string | null;
  maxDescriptionLength: number;
  changed: boolean;
}

interface Computed {
  mode: Mode;
  isCustom: boolean;
}

interface Methods {
  setChanged(b: boolean): void;
  input(): void;
  inputItem(plot: ExtensionPlot, plots: ExtensionPlot[]): Promise<void>;
  deleteItem(plot: ExtensionPlot, index: number): void;
  onDragEnd(): void;
  addPlotGroup(): void;
  deletePlotGroup(index: number): void;
  generateInitialPlot(): ExtensionPlot;
  save(): void;
}

type ExtensionPlot = Plots & {
  isInitial?: boolean;
};
